"use client";

import React from "react";
import { Loader, Send } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function GetInTouch() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="get-in-touch" ref={ref} className={`relative w-full bg-background py-32 transition-opacity duration-700 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20 items-start">
          {}
          <div className="w-full lg:w-1/2 flex flex-col gap-8 relative">
            <div className="space-y-4">
              <h1 className="font-serif font-bold text-5xl md:text-6xl text-primary tracking-normal">
                Let’s Talk
              </h1>
              <p className="font-sans italic font-light text-xl text-foreground/80 max-w-md leading-relaxed !w-[500px]">
                Whether you’re planning a luxury tour or just have a question,
                I’d love to hear from you. Let’s create something memorable
                together.
              </p>
            </div>

            <div className="relative mt-8 w-full max-w-md self-center lg:self-start">
              {}
              <div className="absolute -top-6 -left-6 w-full h-full bg-secondary rounded-lg -z-10" />
              
              {}
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1553131/img/contact_headshot_professional.png" alt="Professional headshot" className="rounded-lg shadow-lg object-cover aspect-[4/5] !h-[500.523px] !w-[576px]" />
            </div>
          </div>

          {}
          <div className="w-full lg:w-1/2 bg-card/50 backdrop-blur-sm rounded-xl p-1 md:p-6">
            <FormProvider formId="get_in_touch_form_contact" sectionName="get-in-touch" defaultValues={{
            name: "",
            email: "",
            phone: "",
            subject: "",
            message: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            message: {
              required: true
            }
          }} submitText="Send Message" successMessage="Thank you! We'll get back to you shortly." errorMessage="Something went wrong. Please try again." className="w-full">
              <ContactForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-8 text-center space-y-4 bg-secondary/30 rounded-lg border border-border">
        <div className="size-12 rounded-full bg-green-100 flex items-center justify-center text-green-600">
          <Send className="size-6" />
        </div>
        <h3 className="text-xl font-serif font-bold text-primary">
          Message Sent!
        </h3>
        <p className="text-muted-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-2">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans text-foreground">Your Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" className="bg-background border-input focus-visible:border-primary focus-visible:ring-primary/20 transition-colors h-12" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans text-foreground">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className="bg-background border-input focus-visible:border-primary focus-visible:ring-primary/20 transition-colors h-12" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans text-foreground">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+972 50-123-4567" className="bg-background border-input focus-visible:border-primary focus-visible:ring-primary/20 transition-colors h-12" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="subject" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans text-foreground">Subject</FormLabel>
              <FormControl>
                <Input placeholder="e.g., Inquiry about luxury tours" className="bg-background border-input focus-visible:border-primary focus-visible:ring-primary/20 transition-colors h-12" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Message is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-sans text-foreground">Message</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us how we can help you..." className="bg-background border-input focus-visible:border-primary focus-visible:ring-primary/20 transition-colors min-h-[160px] resize-y" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
          <p className="text-destructive text-sm font-medium">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto min-w-[160px] bg-primary text-primary-foreground hover:bg-primary/90 hover:-translate-y-0.5 transition-all duration-300 shadow-md rounded-md h-12 text-base font-medium">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Sending...
          </> : "Send Message"}
      </Button>
    </Form>;
}